import { ColumnDef } from '@tanstack/react-table'
import to from 'await-to-js'
import { DataTable, DataTableProvider } from 'components/DataTable'
import { DataTableContainer } from 'components/DataTable/UI'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { TextPill } from 'components/FbUI/TextPill'
import {
  ActionsDropdown,
  createDeleteAction,
} from 'components/Buttons/ActionsDropdown'
import dialogConfirm from 'components/dialogConfirm'
import FbButton from 'components/FbUI/FbButton'
import { OverflownText } from 'components/OverflownText'
import { Tooltip } from 'components/Tooltip/tooltip'
import { DateCell } from 'components/Tables/CommonTableCells/DateCell'
import { Modal } from 'components/UI/Modal/Modal'
import { useEffect, useMemo, useState } from 'react'
import {
  PiArrowsClockwise,
  PiGear,
  PiKey,
  PiMagnifyingGlass,
  PiPlus,
  PiWarningCircle,
} from 'react-icons/pi'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { type Grant, useDeleteGrant, useListGrants } from 'services/grants'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { getApiErrorDetail } from 'utils/getApiErrorDetail'
import { CONNECTIONS_TABLE_KEY } from '../../../constants/tableQueryKeys'
import { AddAccountModal } from './AddAccountModal'
import { ManageConnectionModal } from './ManageConnectionModal'
import { SelectCalendarModal } from './SelectCalendarModal'

const PAGE_SIZE = 25
const sortableFields = ['email', 'updated_at', 'created_at']

export function ConnectionsTable() {
  return (
    <TableLinkStyleProvider variant="plain">
      <div className="flex flex-col w-full flex-1 min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
        <ColumnsStoreProvider tableKey={CONNECTIONS_TABLE_KEY}>
          <DataTableProvider tableKey={CONNECTIONS_TABLE_KEY}>
            <ConnectionsTableInner />
          </DataTableProvider>
        </ColumnsStoreProvider>
      </div>
    </TableLinkStyleProvider>
  )
}

function ConnectionsTableInner() {
  const linkVariant = useTableLinkVariant()
  const isPlain = linkVariant === 'plain'
  const api = useApiService()
  const [showAddAccountModal, setShowAddAccountModal] = useState(false)
  const [manageGrant, setManageGrant] = useState<Grant | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [calendarCallbackEmail, setCalendarCallbackEmail] = useState<
    string | null
  >(null)
  const [selectCalendarIntegrationId, setSelectCalendarIntegrationId] =
    useState<number | null>(null)
  const { sorting } = dataTableSortingStoreRepo.getStore(
    CONNECTIONS_TABLE_KEY
  )()

  const sortParams = useMemo(() => {
    const params: Record<string, string> = {}
    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }
    return params
  }, [sorting])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const success = urlParams.get('success')
    const error = urlParams.get('error')
    if (success) {
      const features = urlParams.get('features') ?? ''
      const email = urlParams.get('email')
      if (features.includes('calendar') && email) {
        setCalendarCallbackEmail(email)
      } else {
        setShowSuccessModal(true)
      }
      window.history.replaceState({}, '', `${window.location.pathname}`)
    } else if (error) {
      const message = urlParams.get('message')
      if (error === 'insufficient_permissions') {
        toast.error(
          message ||
            'You must allow all requested permissions to integrate your account'
        )
      } else {
        toast.error(
          'There was an issue connecting your account. Make sure you grant the necessary permissions and try again.'
        )
      }
      window.history.replaceState({}, '', `${window.location.pathname}`)
    }
  }, [])

  const [page, setPage] = useState(1)

  const { isFetching, data, refetch } = useListGrants({
    ...sortParams,
    limit: PAGE_SIZE,
    page,
  })

  const deleteGrantMutation = useDeleteGrant()

  // Once grants data loads after a calendar callback, find the matching grant
  // and open the calendar selection modal.
  useEffect(() => {
    if (!calendarCallbackEmail || !data?.results) return
    const grant = data.results.find(
      (g) => g.email === calendarCallbackEmail && g.calendar_integration_id
    )
    if (grant?.calendar_integration_id) {
      setSelectCalendarIntegrationId(grant.calendar_integration_id)
      setCalendarCallbackEmail(null)
    }
  }, [calendarCallbackEmail, data])

  // Client-side search across the current page of results.
  const filteredResults = useMemo(() => {
    const results = data?.results ?? []
    if (!searchQuery) return results
    const q = searchQuery.toLowerCase()
    return results.filter(
      (g) =>
        g.email?.toLowerCase().includes(q) ||
        g.provider?.toLowerCase().includes(q)
    )
  }, [data?.results, searchQuery])

  const onAddAccountContinue = async (
    provider: string,
    features: string[],
    isSharedValue: boolean,
    isCalendarSharedValue: boolean
  ) => {
    const [err, response] = await to(
      api.emailIntegration.getEmailIntegrationAuthUrl({
        provider,
        isShared: isSharedValue,
        isCalendarShared: isCalendarSharedValue,
        features,
      })
    )
    if (err) {
      toast.error('Error connecting account')
      return
    }
    if (response?.auth_url) {
      window.location.href = response.auth_url
    }
  }

  async function handleDelete(grant: Grant) {
    const confirmed = await dialogConfirm(
      'Removing this connection will delete all email and calendar data associated with this account. This action cannot be undone.',
      'Delete Connection?'
    )
    if (!confirmed) return

    const [err] = await to(
      deleteGrantMutation.mutateAsync({ grant_id: grant.grant_id })
    )
    if (err) {
      toast.error('Error deleting connection')
      return
    }
    toast.success('Connection deleted')
  }

  async function handleSync(grant: Grant) {
    if (!grant.email_integration_id) {
      toast.error('No email integration to sync')
      return
    }
    const [err] = await to(
      api.emailIntegration.syncEmailIntegration(grant.email_integration_id)
    )
    if (err) {
      toast.error(getApiErrorDetail(err, 'Error syncing emails'))
      return
    }
    toast.info(
      'Started syncing emails for this account. You will be notified when it is done.'
    )
    await refetch()
  }

  async function handleReauthenticate(grant: Grant) {
    if (!grant.provider) {
      toast.error(
        'Unable to reauthenticate: provider information not available'
      )
      return
    }
    const features: string[] = []
    if (grant.has_email) features.push('email')
    if (grant.has_calendar) features.push('calendar')

    const [err, response] = await to(
      api.emailIntegration.getEmailIntegrationAuthUrl({
        provider: grant.provider,
        isShared: grant.is_email_shared,
        isCalendarShared: grant.is_calendar_shared,
        email: grant.email,
        features,
      })
    )
    if (err) {
      toast.error('Error creating reauthentication URL')
      return
    }
    if (response?.auth_url) {
      window.location.href = response.auth_url
    }
  }

  const columns = useMemo<ColumnDef<Grant, any>[]>(
    () => [
      {
        accessorKey: 'email',
        header: 'Email',
        size: 240,
        meta: isPlain ? { isFirstColumn: true } : undefined,
        cell: (info) => (
          <OverflownText>{info.getValue() as string}</OverflownText>
        ),
      },
      {
        accessorKey: 'provider',
        header: 'Provider',
        size: 120,
        cell: (info) => {
          const value = info.getValue() as string
          if (value === 'google') return 'Google'
          if (value === 'microsoft') return 'Outlook'
          return value
        },
      },
      {
        id: 'features',
        header: 'Features',
        size: 180,
        cell: ({ row }) => {
          const g = row.original
          return (
            <div className="flex gap-1.5">
              {g.has_email && (
                <TextPill textColor="#075985" backgroundColor="#e0f2fe">
                  Email
                </TextPill>
              )}
              {g.has_calendar && (
                <TextPill textColor="#6b21a8" backgroundColor="#f3e8ff">
                  Calendar
                </TextPill>
              )}
            </div>
          )
        },
      },
      {
        id: 'shared',
        header: 'Shared',
        size: 140,
        cell: ({ row }) => {
          const g = row.original
          const parts: string[] = []
          if (g.is_email_shared) parts.push('Email')
          if (g.is_calendar_shared) parts.push('Calendar')
          return parts.length > 0 ? parts.join(', ') : 'No'
        },
      },
      {
        id: 'status',
        header: 'Status',
        size: 140,
        cell: ({ row }) => {
          const g = row.original
          const primaryStatus = g.email_status || g.calendar_status
          if (!primaryStatus) return '-'

          if (primaryStatus === 'synced') {
            return (
              <TextPill textColor="#15803d" backgroundColor="#dcfce7">
                Synced
              </TextPill>
            )
          }
          if (primaryStatus === 'syncing') {
            return (
              <TextPill textColor="#a16207" backgroundColor="#fef9c3">
                Syncing
              </TextPill>
            )
          }
          return (
            <TextPill textColor="#b91c1c" backgroundColor="#fee2e2">
              Not Synced
            </TextPill>
          )
        },
      },
      {
        accessorKey: 'is_expired',
        header: 'Connection',
        size: 160,
        cell: (info) => {
          const isExpired = info.getValue() as boolean
          const grant = info.row.original

          if (isExpired) {
            return (
              <Tooltip
                title="Connection broken"
                content="Click to reauthenticate your account"
                placement="top"
              >
                <button
                  type="button"
                  onClick={() => handleReauthenticate(grant)}
                  className="inline-flex items-center gap-1 cursor-pointer"
                >
                  <TextPill textColor="#b91c1c" backgroundColor="#fee2e2">
                    <PiWarningCircle size={12} className="mr-0.5" />
                    Disconnected
                  </TextPill>
                </button>
              </Tooltip>
            )
          }

          return (
            <TextPill textColor="#15803d" backgroundColor="#dcfce7">
              Connected
            </TextPill>
          )
        },
      },
      {
        accessorKey: 'updated_at',
        header: 'Modified',
        size: 140,
        cell: DateCell,
      },
      {
        id: 'actions',
        header: isPlain
          ? () => <div className="w-full text-center">Actions</div>
          : 'Actions',
        size: isPlain ? 70 : 150,
        meta: {
          isLastColumn: true,
          headerDisableOrdering: true,
          rightAlign: !isPlain,
        },
        cell: ({ row }) => {
          const g = row.original
          const actions = [
            {
              label: 'Manage',
              icon: PiGear,
              onClick: () => setManageGrant(g),
            },
            {
              label: 'Reauthenticate',
              icon: PiKey,
              onClick: () => handleReauthenticate(g),
            },
            ...(g.has_email
              ? [
                  {
                    label: 'Sync emails',
                    icon: PiArrowsClockwise,
                    disabled: g.is_expired,
                    disabledReason: g.is_expired
                      ? 'Reauthenticate before syncing'
                      : undefined,
                    onClick: () => handleSync(g),
                  },
                ]
              : []),
            createDeleteAction(() => handleDelete(g)),
          ]
          return (
            <ActionsDropdown
              className={isPlain ? '!w-full justify-center' : undefined}
              actions={actions}
            />
          )
        },
      },
    ],
    [isPlain]
  )

  return (
    <>
      <DataTableContainer className="h-full">
        <DataTable
          tableKey={CONNECTIONS_TABLE_KEY}
          tableHeader={
            <div
              data-table-toolbar
              className="flex flex-row items-center justify-between gap-3 px-4 py-3"
            >
              <div className="relative w-full max-w-md">
                <PiMagnifyingGlass
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search connections..."
                  className="w-full h-9 pl-9 pr-3 text-sm bg-white border border-gray-300 rounded-lg text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-brand-active focus:shadow-[0_0_0_4px_rgba(8,116,67,0.2)]"
                />
              </div>
              <FbButton
                variant="primary"
                size="sm"
                onClick={() => setShowAddAccountModal(true)}
                className="!bg-brand-bright !text-white hover:!bg-brand-active shrink-0"
              >
                <PiPlus size={16} />
                Add Account
              </FbButton>
            </div>
          }
          data={filteredResults}
          columns={columns}
          sortableFields={sortableFields}
          isPaginationEnabled={!searchQuery}
          paginationOptions={{
            totalRows: searchQuery
              ? filteredResults.length
              : (data?.count ?? 0),
            pageSize: PAGE_SIZE,
            setPage,
            page,
            isPaginationLoading: isFetching,
          }}
          loading={isFetching}
          stickyLastColumn={isPlain}
        />
      </DataTableContainer>
      <AddAccountModal
        key={String(showAddAccountModal)}
        open={showAddAccountModal}
        onClose={() => setShowAddAccountModal(false)}
        onContinue={onAddAccountContinue}
      />
      <ManageConnectionModal
        key={manageGrant?.grant_id ?? 'none'}
        open={!!manageGrant}
        grant={manageGrant}
        onClose={() => setManageGrant(null)}
        onRefetch={() => void refetch()}
      />
      <SelectCalendarModal
        open={!!selectCalendarIntegrationId}
        integrationId={selectCalendarIntegrationId}
        onClose={() => {
          setSelectCalendarIntegrationId(null)
          setShowSuccessModal(true)
        }}
        onSelected={() => {
          setSelectCalendarIntegrationId(null)
          void refetch()
        }}
      />
      {showSuccessModal && (
        <Modal
          open={showSuccessModal}
          title="Success"
          description="Account connected successfully"
          onOpenChange={(open) => !open && setShowSuccessModal(false)}
          onAccept={() => setShowSuccessModal(false)}
          acceptButtonText="Close"
          hideCancelButton
        />
      )}
    </>
  )
}
